import styled from '@emotion/styled'
import { MODULE_TYPE_MAPPING } from '@pubstack/common/src/catalogItem'
import { MultipleEditPayload, PrebidModule, PrebidModuleDataBySite, PrebidModuleType } from '@pubstack/common/src/prebidModule'
import { byLabel } from '@pubstack/common/src/sort'
import { ReactNode } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useCatalogModules } from '~/api/catalog.api'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { BaseInputElement, Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { PageContent } from '~/components/PageContent'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { Status } from '~/components/Status'
import { handleTableSearch } from '~/components/table/Table'
import { Tooltip } from '~/components/Tooltip'
import { WidgetMessage } from '~/components/WidgetMessage'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { ModuleHeader } from '~/feature-components/ModuleHeader'
import { usePbjsVersionHeaderItems } from '../bidderParams/usePbjsVersionHeaderItems'
import { CollapsiblePrebidModule } from './CollapsiblePrebidModule'
import { PureAdmPrebidModuleEditFlyout } from './PureAdmPrebidModuleEditFlyout'
import { PureAdmPrebidModuleHelpFlyout } from './PureAdmPrebidModuleHelpFlyout'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

const Filter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  > div {
    display: flex;
    flex-direction: row;
    gap: 20px;
   };
  ${BaseInputElement} {
    width: 286px;
  };
`
const GreenbidsMessage = styled.div`
  padding: 0 25px 0 0;
`

const SitesModule = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const filterStatusOptions: SelectOptionProp[] = [
  {
    label: 'All status',
    value: 'all',
    trackingLabel: 'adm-integrations-modules-multiple-edition-no-filter-apply',
  },
  {
    label: 'Active',
    value: 'active',
    trackingLabel: 'adm-integrations-modules-multiple-edition-filter-active-apply',
  },
  {
    label: 'Not used',
    value: 'inactive',
    trackingLabel: 'adm-integrations-modules-multiple-edition-filter-inactive-apply',
  },
]

export type PureAdmPrebidModulePageProps = {
  prebidModule: PrebidModule
  data: PrebidModuleDataBySite<PrebidModuleType>[]
  breadcrumbs: ReactNode
  helpText: ReactNode
  isLoading: boolean
  updateSiteData: (data: PrebidModuleDataBySite<PrebidModuleType>) => Promise<void>
  baseCDNUrl: string
  onBackClick: () => void
  onMultipleEdit: (payload: MultipleEditPayload<PrebidModuleType>) => Promise<void>
  enabledGreenbidsAnalytics?: PrebidModuleDataBySite<'Analytics'>[]
}

export const PureAdmPrebidModulePage = ({
  prebidModule,
  breadcrumbs,
  data,
  helpText,
  isLoading,
  updateSiteData,
  baseCDNUrl,
  onBackClick,
  onMultipleEdit,
  enabledGreenbidsAnalytics,
}: PureAdmPrebidModulePageProps) => {
  const flyout = useGlobalFlyout()
  // Multiple edit writes this module onto every media at once, so it is the owner's — the same gate each
  // media's own panel below carries.
  const { canEdit, disabledReason } = useScopePermissions()

  const { control: filterControl } = useForm<{ search: string; status: string }>({
    defaultValues: {
      search: '',
      status: 'all',
    },
  })
  const { search, status } = useWatch({ control: filterControl })
  const searchedData = handleTableSearch(data, search || '', ['label']).sort(byLabel)
  const filteredData = searchedData.filter((a) => (status === 'active' ? a.enabled : status === 'inactive' ? !a.enabled : true))

  // if any site settings is used, the module is used
  const isUsed = data.some((data) => data.enabled)

  const applyMultipleEdit = async (payload: MultipleEditPayload<PrebidModuleType>) => {
    await onMultipleEdit(payload)
    flyout.close()
  }
  const isGreenbidRtdModule = prebidModule.code === 'greenbidsRtdProvider'
  const { moduleId } = useParams()
  const { data: catalogData } = useCatalogModules()
  const catalogItem = catalogData?.find((item) => item.id === moduleId && item.type === 'prebidModule')
  const scopePbjsVersion = catalogItem?.type === 'prebidModule' ? catalogItem.scopePbjsVersion : undefined
  const pbjsVersionHeaderItems = usePbjsVersionHeaderItems(prebidModule.pbjsMinRequiredVersion, scopePbjsVersion, 'module')
  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <>
          <Button variant={'tertiary'} onClick={onBackClick} trackingLabel={'adm-forms-back'}>
            Back
          </Button>
          <Button
            variant={'tertiary'}
            onClick={() =>
              flyout.open(
                PureAdmPrebidModuleHelpFlyout,
                { color: Colors.Forest, width: `816px`, closeTrackingLabel: 'adm-bidder-rules-detail-cancel' },
                {
                  customCheckText: helpText,
                }
              )
            }
            iconName={'help'}
            trackingLabel={'adm-integrations-modules-help'}
          >
            Help
          </Button>
        </>
      }
    >
      <Container>
        <ModuleHeader
          catalogItemCode={prebidModule.code}
          catalogItemType={'prebidModule'}
          isLoading={isLoading}
          status={<Status state={isUsed ? 'active' : 'inactive'}>{isUsed ? 'Active' : 'Not used'}</Status>}
          baseCDNUrl={baseCDNUrl}
          extraHeaderItems={[{ label: 'Module type', value: MODULE_TYPE_MAPPING[prebidModule.type].longLabel }, ...pbjsVersionHeaderItems]}
        />

        {isGreenbidRtdModule && (
          <GreenbidsMessage>
            <WidgetMessage icon="tips" width={'100%'} message={'You need to enable greenbids prebid module analytics in order to enable greenbids RTD.'} />
          </GreenbidsMessage>
        )}

        <Filter>
          <div>
            <Input type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search'} name={'search'} control={filterControl} />
            <Select name={'status'} options={filterStatusOptions} label={'Status'} control={filterControl} />
          </div>
          <Tooltip title={disabledReason} disabled={canEdit}>
            <Button
              variant={'secondary'}
              disabled={!canEdit}
              onClick={() =>
                flyout.open(
                  PureAdmPrebidModuleEditFlyout,
                  {
                    color: Colors.Kaiminus,
                    width: '612px',
                    closeTrackingLabel: 'adm-integrations-modules-multiple-edition-close',
                  },
                  {
                    prebidModule,
                    data: [...data].sort(byLabel),
                    enabledGreenbidsAnalytics,
                    onMultipleEdit: applyMultipleEdit,
                  }
                )
              }
              trackingLabel={'adm-integrations-modules-edit'}
            >
              Multiple edit
            </Button>
          </Tooltip>
        </Filter>

        <SitesModule>
          {filteredData.map((config) => {
            if (isGreenbidRtdModule) {
              const greenbidsAnalyticsModuleForCurrentSite = enabledGreenbidsAnalytics?.find((greenbidSiteConfig) => greenbidSiteConfig.siteId === config.siteId)
              return (
                <CollapsiblePrebidModule
                  prebidModule={prebidModule}
                  key={config.label}
                  data={config}
                  isLoading={isLoading}
                  updateSiteData={updateSiteData}
                  isForbidden={!greenbidsAnalyticsModuleForCurrentSite?.enabled}
                />
              )
            } else {
              return <CollapsiblePrebidModule prebidModule={prebidModule} key={config.label} data={config} isLoading={isLoading} updateSiteData={updateSiteData} />
            }
          })}
        </SitesModule>
      </Container>
    </PageContent>
  )
}
